export const COURSE_DEBT_REASON = 'course_debt';

export interface ICourseDebtError {
  message: string;
  courseId: number | null;
  /** Сумма всех просроченных счетов по курсу. `null`, если просроченный счёт не нашёлся. */
  amountDue: number | null;
  currency: string | null;
  invoiceNumber: string | null;
  /** Реквизиты со счёта, иначе общий текст колледжа. Может прийти `null` — это норма. */
  paymentDetails: string | null;
}

const DEFAULT_COURSE_DEBT_MESSAGE =
  'Access to this course is suspended until the outstanding invoice is paid';

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const readNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/**
 * Отказ из-за непогашенной задолженности: 403 с `details.reason === 'course_debt'`.
 *
 * Кроме причины отказ несёт сумму и реквизиты, поэтому блоку долга не нужны
 * дополнительные запросы. Всё, кроме `reason` и `courseId`, может отсутствовать:
 * так бывает, когда просроченный счёт по курсу не нашёлся.
 *
 * Закрывается только курс с долгом — остальные работают, поэтому общая заглушка
 * «нет доступа» здесь не годится: человек не поймёт, что делать.
 *
 * Не путать с разлогином по 403: тот срабатывает только на `error.message === 'Forbidden'`,
 * и наш случай туда не попадает.
 */
export const getCourseDebtError = (error: unknown): ICourseDebtError | null => {
  const record = asRecord(error);
  if (!record) return null;

  // Ошибка либо уже нормализована в baseQueryWithInterceptor, либо пришла сырым телом.
  const nested = asRecord(record.error);
  const details = asRecord(record.details) ?? asRecord(nested?.details);

  if (!details || details.reason !== COURSE_DEBT_REASON) return null;

  return {
    message: readString(record.message ?? nested?.message) ?? DEFAULT_COURSE_DEBT_MESSAGE,
    courseId: readNumber(details.courseId),
    amountDue: readNumber(details.amountDue),
    currency: readString(details.currency),
    invoiceNumber: readString(details.invoiceNumber),
    paymentDetails: readString(details.paymentDetails),
  };
};
